/**
 * Customer audit-timeline rows for one ticket.
 *
 * Wraps GET /api/v1/tickets/{n}/events - the customer-safe variant
 * (agent identity scrubbed to "Buyist Support", to_value whittled,
 * private-comment events filtered out server-side). Always major-only;
 * there is no Show-All toggle on the customer surface.
 */
import { useQuery, type UseQueryResult } from "@tanstack/react-query";

import api from "@/lib/api";
import type { CustomerTicketEventResponse } from "@/types/api";

export function customerTicketEventsQueryKey(
  ticketNumber: number,
  limit: number,
): readonly unknown[] {
  return ["customer", "tickets", "events", ticketNumber, limit] as const;
}

export function customerTicketEventsQueryKeyPrefix(
  ticketNumber: number,
): readonly unknown[] {
  return ["customer", "tickets", "events", ticketNumber] as const;
}

export function useCustomerTicketEvents(
  ticketNumber: number,
  options: { enabled?: boolean; limit?: number } = {},
): UseQueryResult<CustomerTicketEventResponse[]> {
  const limit = options.limit ?? 20;
  return useQuery({
    queryKey: customerTicketEventsQueryKey(ticketNumber, limit),
    enabled: options.enabled !== false,
    queryFn: async () => {
      const response = await api.get<CustomerTicketEventResponse[]>(
        `/tickets/${ticketNumber}/events`,
        { params: { limit } },
      );
      return response.data;
    },
  });
}
